import { InteractionRequiredAuthError, BrowserAuthError, PublicClientApplication } from '@azure/msal-browser';
import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { applicationSlot } from '../applicationSlot';
import { loginRequest, msalConfig } from './msalConfig';

declare const REACT_APP_API_ENDPOINT: string | undefined;
declare const REACT_APP_API_BLUE_ENDPOINT: string | undefined;

export function apiEndpoint() {

    if (typeof REACT_APP_API_ENDPOINT === "undefined" || typeof REACT_APP_API_BLUE_ENDPOINT === "undefined")
        return "https://mock-api";

    return applicationSlot() === "blue"
        ? REACT_APP_API_BLUE_ENDPOINT || ""
        : REACT_APP_API_ENDPOINT || "";
}

const beyonAxiosInstance = axios.create({
    baseURL: apiEndpoint(),
    headers: {
        "Content-Type": "application/json",
    }
});

export const isFramed = window.self !== window.top;

export const createAxiosInstance = (): AxiosInstance => {

    // Cache token promise to prevent multiple simultaneous token requests    
    beyonAxiosInstance.interceptors.request.use(interceptor);

    return beyonAxiosInstance;

};

const client = createAxiosInstance();

export const axiosBeyonClient = client;

export type Token = {
    access_token: string,
    expires_in: number,
    scope: string,
    token_type: string
}

let tokenAcquisitionPromise: Promise<Token | null> | null = null;

async function interceptor(config: InternalAxiosRequestConfig<any>) {

    try {
        if (!tokenAcquisitionPromise)
            tokenAcquisitionPromise = getBeyonToken();

        const token = await tokenAcquisitionPromise;

        if (token)
            config.headers.Authorization = `Bearer ${token.access_token}`;

        return config;
    } catch (error) {
        console.error('CM > Token acquisition failed:', error);
        return Promise.reject(error);
    }
}

async function getBeyonToken(): Promise<Token> {

    const solutionId = window.location.pathname.split("/")[2];

    const cacheKey = `beyon-accessToken-${solutionId}`;

    let token = getBeyonTokenFromCache(cacheKey);
    if (token)
        return token;

    if (isFramed) {
        console.info("CM > Application is running in an iframe, trying to load a token from the window parent")
        token = await getBeyonTokenFromParentWindow(solutionId);
    }
    else {
        console.info("CM > Application is running from the window.top, trying to load a token from B2C")
        token = await getBeyonTokenFromB2cLogin(solutionId);
    }

    if (token) {
        cacheBeyonToken(cacheKey, token);
        return token;
    }

    throw new Error("CM > Could not load a Beyon token")
}

function getBeyonTokenFromCache(cacheKey: string): Token | undefined {
    const storageValue = localStorage.getItem(cacheKey);
    if (!storageValue) {
        console.info("CM > No Beyon token found in the cache.")
        return undefined;
    }

    let token: Token;

    try {
        token = JSON.parse(storageValue);
    } catch (error) {
        console.error("CM > Cached Beyon token is invalid.")
        return undefined;
    }

    if (getTimeTillRefresh(token) <= 0) {
        console.info("CM > Beyon token has expired.")
        return undefined;
    }

    return token;

    function getTimeTillRefresh(token: Token) {
        const timeToExpiry = (token.expires_in * 1000) - Date.now();
        return Math.max(timeToExpiry - 5000, 0);
    };
}

function cacheBeyonToken(cacheKey: string, token: Token) {
    localStorage.setItem(cacheKey, JSON.stringify(token));
}

async function getBeyonTokenFromB2cLogin(solutionId: string): Promise<Token | undefined> {

    // Get a Token from Azure B2C
    try {
        const b2cAccessToken = await getB2cAccessToken();

        // Exchange the B2C token with a Beyon token
        const zeusClient = axios.create({
            baseURL: "https://api.beca.digital/zeus/v1",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                Authorization: `Bearer ${b2cAccessToken}`
            }
        });
        
        const urlEncodedBody = new URLSearchParams({
            grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
            subject_token: b2cAccessToken,
            solution_id: solutionId
        }).toString();
        
        const response = await zeusClient.post<string, AxiosResponse<Token>>("/oauth/exchange", urlEncodedBody);
        return response.data;
    } catch (error) {
        console.error(error);
    }
};

type BeyonIFrameEvent<TPayload> = {
    eventType: string;
    payload: TPayload;
}

async function getBeyonTokenFromParentWindow(solutionId: string): Promise<Token> {
    return new Promise<Token>((resolve, reject) => {

        const messageHandler = (event: MessageEvent<BeyonIFrameEvent<Token>>) => {
            if (event.data.eventType === "BEYON_SEND_TOKEN") {
                clearTimeout(timeoutId);
                console.info("CM > Beyon Token retrieved from the window parent.")
                window.removeEventListener("message", messageHandler);
                resolve(event.data.payload);
            }
        };

        window.addEventListener('message', messageHandler);

        console.info("CM > Request Beyon Token from window parent.")
        window.parent.postMessage({
            eventType: "BEYON_GET_TOKEN",
            payload: {
                solutionId
            }
        }, "*");

        const timeoutId = setTimeout(() => {
            window.removeEventListener("message", messageHandler);
            const error = "CM > Beyon token request from the parent window expired.";
            console.info(error)
            reject(error);
        }, 2000);
    });
}

async function getB2cAccessToken(): Promise<string> {

    console.info("CM > Initialize MSAL clientApp");
    const msalInstance = await PublicClientApplication.createPublicClientApplication(msalConfig);

    console.info("CM > Initialize MSAL instance");
    await msalInstance.initialize();


    const accounts = msalInstance.getAllAccounts();

    const tokenRequest = {
        scopes: loginRequest.scopes,
        account: accounts[0],
        loginHint: accounts[0]?.username,
    };

    await msalInstance.handleRedirectPromise();

    try {
        console.info("CM > Acquire B2C token silently");
        const response = await msalInstance.acquireTokenSilent(tokenRequest);
        console.info("CM > B2C token retrieved from B2C");
        return response.accessToken;
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError || BrowserAuthError) {
            console.info("CM > Failed to aquire token silently. Aquire B2C token from redirect");
            localStorage.clear();
            await msalInstance.acquireTokenRedirect(tokenRequest);
        }
        throw error;
    }
}